import React, { useState } from 'react';
import { cn } from '../../../lib/utils';
import { Card, Skel } from './parts';
import { int, SiteHealth, Visitors } from './api';

type Vital = { key: 'lcp' | 'inp' | 'cls'; name: string; what: string; good: number; poor: number; fmt: (n: number) => string };

// Google's Core Web Vitals thresholds, judged at the 75th percentile.
const VITALS: Vital[] = [
  { key: 'lcp', name: 'Loading', what: 'Largest Contentful Paint: time until the main content shows. Good is 2.5 s or less.', good: 2500, poor: 4000, fmt: (n) => `${(n / 1000).toFixed(1)} s` },
  { key: 'inp', name: 'Responsiveness', what: 'Interaction to Next Paint: delay after a tap or click. Good is 200 ms or less.', good: 200, poor: 500, fmt: (n) => `${Math.round(n)} ms` },
  { key: 'cls', name: 'Stability', what: 'Cumulative Layout Shift: how much the page jumps while loading. Good is 0.1 or less.', good: 0.1, poor: 0.25, fmt: (n) => n.toFixed(2) },
];

const rating = (v: Vital, n: number) => (n <= v.good ? { label: 'Good', tone: 'good' } : n <= v.poor ? { label: 'Needs work', tone: 'warn' } : { label: 'Poor', tone: 'bad' });
/** Marker position on a track whose three zones are equal thirds (good, needs work, poor). */
const place = (v: Vital, n: number) => {
  if (n <= v.good) return (n / v.good) * 33.3;
  if (n <= v.poor) return 33.3 + ((n - v.good) / (v.poor - v.good)) * 33.3;
  return Math.min(98, 66.6 + ((n - v.poor) / v.poor) * 33.3);
};

const Friction: React.FC<{ h: SiteHealth }> = ({ h }) => {
  const worst = h.pages[0];
  return (
    <div className="flex flex-col gap-2">
      <h3 className="oa-sub">Friction</h3>
      <div className="oa-kv">
        <span title="Several fast clicks in one spot, usually on something that did not respond">
          Rage clicks <b className={cn(h.rage > 0 && '!text-[var(--oa-warn)]')}>{int(h.rage)}</b>
        </span>
        <span title="Clicks on something that looks clickable but did nothing">
          Dead clicks <b>{int(h.dead)}</b>
        </span>
        <span title="JavaScript errors visitors hit">
          Errors <b className={cn(h.errors > 0 && '!text-[var(--oa-bad)]')}>{int(h.errors)}</b>
        </span>
      </div>
      {worst ? (
        <p className="truncate text-[12px] text-[var(--oa-faint)]" title={h.pages.map((p) => `${p.path}: ${p.rage} rage, ${p.dead} dead, ${p.errors} errors`).join('\n')}>
          Most on {worst.path === '/' ? 'the home page' : worst.path}: {[worst.rage && `${worst.rage} rage`, worst.dead && `${worst.dead} dead`, worst.errors && `${worst.errors} errors`].filter(Boolean).join(', ')}
        </p>
      ) : null}
    </div>
  );
};

/** Core Web Vitals from real visits, on a good / needs work / poor track, plus click friction. */
export const SiteHealthCard: React.FC<{ v: Visitors | null; loading: boolean }> = ({ v, loading }) => {
  const [hover, setHover] = useState<Vital['key'] | null>(null);

  if (!v) {
    return (
      <Card i={8} area="spd" title="Site speed">
        {loading ? <Skel className="min-h-[220px] flex-1" /> : <p className="oa-cap">Site speed appears once PostHog is connected.</p>}
      </Card>
    );
  }

  const h = v.health;
  const shown = VITALS.find((x) => x.key === hover);

  return (
    <Card i={8} area="spd" title="Site speed" actions={<span className="oa-cap text-[12px]">{h.samples ? `${int(h.samples)} readings` : ''}</span>}>
      {h.samples === 0 ? (
        <p className="oa-cap flex flex-1 items-center">No speed readings in this period.</p>
      ) : (
        <ul className="oa-vitals flex-1" onMouseLeave={() => setHover(null)}>
          {VITALS.map((x, i) => {
            const n = h[x.key];
            const r = n === null ? null : rating(x, n);
            return (
              <li key={x.key} style={{ ['--i' as string]: i, ['--st' as string]: r ? `var(--oa-${r.tone})` : 'var(--oa-faint)' } as React.CSSProperties} onMouseEnter={() => setHover(x.key)} className={cn(hover === x.key && 'is-on')}>
                <span className="text-[13px]">{x.name}</span>
                <b className="oa-num text-[15px]">{n === null ? '–' : x.fmt(n)}</b>
                <span className="oa-chat-st">{r ? r.label : 'No data'}</span>
                <span className="oa-vtrack" aria-hidden="true">
                  <i />
                  <i />
                  <i />
                  {n !== null ? <em style={{ left: `${place(x, n)}%` }} /> : null}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      <p className="min-h-[2.9em] text-[12px] leading-[1.45] text-[var(--oa-faint)]">{shown ? shown.what : 'Typical visit (75th percentile), measured in visitors’ own browsers. Hover a row for what it means.'}</p>
      <Friction h={h} />
    </Card>
  );
};
